import { useRef, useState, type FormEvent } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Link from '@mui/material/Link'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { ApiError, api } from '@jupi/ui'
import { AuthCard } from '@jupi/ui'
import { Turnstile, type TurnstileHandle } from '@jupi/ui'
import { TURNSTILE_SITE_KEY } from '@jupi/ui'

// "Forgot password?". The answer is the same whether or not the address
// has an account, so the page can only ever say "if it is a member's".
export default function ForgotPasswordPage() {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [sentTo, setSentTo] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [turnstileToken, setTurnstileToken] = useState<string | undefined>(undefined)
  const turnstileRef = useRef<TurnstileHandle>(null)

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await api.post('/api/auth/password/forgot', { email, turnstileToken })
      setSentTo(email.trim().toLowerCase())
    } catch (err) {
      turnstileRef.current?.reset()
      setTurnstileToken(undefined)
      setError(err instanceof ApiError && err.status === 429 ? t('auth.tooManyAttempts') : t('common.error'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthCard title={t('auth.forgotTitle')} subtitle={t('app.intra')}>
      {sentTo ? (
        <Stack spacing={2}>
          <Alert severity="success">{t('auth.forgotSent', { email: sentTo })}</Alert>
          <Button component={RouterLink} to="/login" variant="outlined" fullWidth>
            {t('auth.backToSignIn')}
          </Button>
        </Stack>
      ) : (
        <form onSubmit={handleSubmit} noValidate>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {t('auth.forgotIntro')}
            </Typography>
            {error ? <Alert severity="error">{error}</Alert> : null}
            <TextField
              label={t('auth.email')}
              type="email"
              autoComplete="username"
              autoFocus
              required
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            {TURNSTILE_SITE_KEY ? (
              <Turnstile
                ref={turnstileRef}
                siteKey={TURNSTILE_SITE_KEY}
                onToken={setTurnstileToken}
                onExpire={() => setTurnstileToken(undefined)}
                onError={() => setTurnstileToken(undefined)}
              />
            ) : null}
            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={busy || !email.includes('@') || (Boolean(TURNSTILE_SITE_KEY) && !turnstileToken)}
            >
              {busy ? t('auth.sending') : t('auth.sendLink')}
            </Button>
            <Link component={RouterLink} to="/login" variant="body2" sx={{ alignSelf: 'center' }}>
              {t('auth.backToSignIn')}
            </Link>
          </Stack>
        </form>
      )}
    </AuthCard>
  )
}
